import type { RegionOption } from '@main/core-ui';
import { iso31662Messages, isoCountryCodeMessages } from '@main/core-ui';
import {
  ISO_31662,
  IsoCountryCode,
  IsoCountrySubdivisionCode,
} from '@transcend-io/privacy-types';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { dataSubjectRequestFormMessages as messages } from './messages';

const allCountryCodes = Object.values(IsoCountryCode);

interface DropdownOption {
  /** region code for option */
  code: IsoCountryCode | IsoCountrySubdivisionCode;
  /** text displayed in dropdown */
  text: string;
}

const sortOptions = (options: Array<DropdownOption>): Array<DropdownOption> =>
  options.sort((prev, curr) =>
    prev.text === curr.text ? 0 : prev.text > curr.text ? 1 : -1,
  );

/**
 * type guard for CountryCode
 */
function isCountryCode(
  code: IsoCountryCode | IsoCountrySubdivisionCode | string,
): code is IsoCountryCode {
  return IsoCountryCode[code as IsoCountryCode] !== undefined;
}

/**
 * type guard for SubRegionCode
 */
function isSubRegionCode(
  code: IsoCountryCode | IsoCountrySubdivisionCode | string,
): code is IsoCountrySubdivisionCode {
  return ISO_31662[code as IsoCountrySubdivisionCode] !== undefined;
}

export interface NativeRegionSelectProps {
  /** regions allowed in list */
  allowedRegions: Array<string>;
  /** on country or subdivision selection made */
  onChange: (selected: RegionOption) => void;
  /** value of country and subdivision combo */
  value: RegionOption;
}

export const NativeRegionSelect: React.FC<NativeRegionSelectProps> = ({
  allowedRegions,
  onChange,
  value,
}) => {
  const { formatMessage } = useIntl();

  const primaryOptions: Array<DropdownOption> = useMemo(() => {
    if (allowedRegions.length === 0) {
      return sortOptions(
        allCountryCodes.map((code) => ({
          code,
          text: formatMessage(isoCountryCodeMessages[code]),
        })),
      );
    }
    const includedRegions = allowedRegions.filter((code) =>
      isCountryCode(code)
        ? allCountryCodes.includes(code)
        : isSubRegionCode(code)
          ? !allowedRegions.includes(ISO_31662[code].parent)
          : false,
    ) as Array<IsoCountryCode | IsoCountrySubdivisionCode>;
    return sortOptions(
      includedRegions.map((code) =>
        isCountryCode(code)
          ? {
              code,
              text: formatMessage(isoCountryCodeMessages[code]),
            }
          : {
              code,
              text: `${ISO_31662[code].parent}: ${formatMessage(
                iso31662Messages[code],
              )}`,
            },
      ),
    );
  }, [allowedRegions]);

  const secondaryOptions: Array<DropdownOption> = useMemo(
    () =>
      sortOptions(
        Object.values(IsoCountrySubdivisionCode)
          .filter(
            (code) =>
              allowedRegions.length === 0 &&
              ISO_31662[code].parent === value.country,
          )
          .map((code) => ({
            code,
            text: formatMessage(iso31662Messages[code]),
          })),
      ),
    [value?.country],
  );

  const primaryRegionText = formatMessage(messages.region);
  const subDivisionText = formatMessage(messages.subDivision);

  return (
    <div
      role="group"
      style={{
        width: '300px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        border: 'none',
      }}
    >
      <select
        name={primaryRegionText}
        required
        style={{ width: '100%', border: 'none' }}
        value={
          allowedRegions.length > 0
            ? value.countrySubDivision || value.country
            : value.country
        }
        aria-label={primaryRegionText}
        onChange={(evt) => {
          const selectedValue = evt.target.value;
          if (isCountryCode(selectedValue)) {
            onChange({
              country: selectedValue,
              countrySubDivision: undefined,
            });
          } else if (isSubRegionCode(selectedValue)) {
            onChange({
              country: ISO_31662[selectedValue].parent as IsoCountryCode,
              countrySubDivision: selectedValue,
            });
          }
        }}
      >
        <option>{primaryRegionText}</option>
        {primaryOptions.map((o) => (
          <option key={o.code} value={o.code}>
            {o.text}
          </option>
        ))}
      </select>
      {allowedRegions.length === 0 && secondaryOptions.length > 0 && (
        <select
          name={subDivisionText}
          style={{ width: '100%', border: 'none' }}
          value={value.countrySubDivision}
          disabled={!value.country}
          aria-label={subDivisionText}
          onChange={(evt) => {
            onChange({
              country: value.country,
              countrySubDivision: evt.target.value as IsoCountrySubdivisionCode,
            });
          }}
        >
          <option>{subDivisionText}</option>
          {secondaryOptions.map((o) => (
            <option key={o.code} value={o.code}>
              {o.text}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};
